<template>
    <td
        class="uik-table-cell"
        :class="{
            'uik-table-cell__sticky': sticky,
            'uik-table-cell__hide--desktop-small': hide === 'd',
            'uik-table-cell__hide--tablet': hide === 't',
            'uik-table-cell__hide--mobile': hide === 'm',
            'uik-table-cell__seamless': type === 'seamless',
            'uik-table-cell--loading': isLoading
        }"
    >
        <div class="uik-table-cell__container">
            <UikLoader
                v-if="isLoading"
                class="uik-table-cell__loader"
                type="dots"
                color="primary"
                :size="0.45"
            />
            <div
                class="uik-table-cell__content"
                :class="{
                    'uik-table-cell__align--center': align === 'center',
                    'uik-table-cell__align--right': align === 'right',
                    'uik-table-cell__align--around': align === 'around',
                    'uik-table-cell__align--between': align === 'between',
                    'uik-table-cell__align--even': align === 'even',
                    'uik-table-cell__text--small': isStyle('small'),
                    'uik-table-cell__text--base': isStyle('base'),
                    'uik-table-cell__text--fade': isStyle('fade'),
                    'uik-table-cell__text--code': isStyle('code'),
                    'uik-table-cell__text--bold': isStyle('bold'),
                    'uik-table-cell__text--primary': isStyle('primary'),
                    'uik-table-cell__text--nowrap': isStyle('nowrap'),
                    'uik-table-cell__avatars': type === 'avatars'
                }"
            >
                <slot/>
            </div>
        </div>
    </td>
</template>

<script>
export default {
    name: "UikTd",
    props: {
        /**
         * Defines alignment of content
         */
        align: { type: String, default: "left" },
        /**
         * Is cell sticky
         */
        sticky: { type: Boolean, default: false },
        /**
         * Style properties of plain text inside the cell
         */
        text: { type: [Array, String], default: "" },
        /**
         * Defines on which responsive breakpoint cell disappears
         */
        hide: { type: String, default: "" },
        /**
         * Defines additional style customizations based on content
         */
        type: { type: String, default: "" },
        /**
         * Is component loading
         */
        isLoading: { type: Boolean, default: false }
    },
    methods: {
        /**
         * Checks if plain text has a preset style
         *
         * @param { String } - style to check
         *
         * @return { Boolean }
         */
        isStyle (style) {
            return this.text === style || (Array.isArray(this.text) && this.text.includes(style))
        }
    }
}
</script>

<style lang="scss">
.uik-table-cell {
  padding: 0;
  animation: table-cell 0.2s;
  @keyframes table-cell {
      from {
          // transform: scale(0.95);
          opacity: 0;
      }
      to {
          // transform: none;
          opacity: 1;
      }
  }
  .uik-table-cell__container {
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: center;
    position: relative;
    .uik-table-cell__content {
      display: flex;
      flex-flow: row nowrap;
      justify-content: flex-start;
      align-items: center;
      text-align: left;
      width: 100%;
      padding: 15px 10px;
      font-size: 14px;
      line-height: 18px;
      font-weight: 400;
      color: $textMain;
      min-height: 70px;
      &.uik-table-cell__align {
        &--center {
          justify-content: center;
          text-align: center;
        }
        &--right {
          justify-content: flex-end;
          text-align: right;
        }
        &--around {
          justify-content: space-around;
          text-align: center;
        }
        &--even {
          justify-content: space-evenly;
          text-align: center;
        }
      }
      &.uik-table-cell__text {
        &--small {
          font-size: 12px;
          line-height: 14px;
        }
        &--base {
          color: $textBase;
        }
        &--fade {
          color: $textSecondary;
        }
        &--code {
          font-family: monospace;
        }
        &--bold {
          font-weight: 500;
        }
        &--primary {
          color: $primary;
        }
        &--nowrap {
          white-space: nowrap;
        }
      }
      a {
        color: $textMain;
        transition: all 0.15s;
        &:hover {
            color: $primary;
        }
      }
      .uik-avatar__wrapper {
        .uik-avatar__info {
          .uik-avatar__name, .uik-avatar__textBottom {
            display: block;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            max-width: 200px;
          }
        }
      }
      &.uik-table-cell__avatars {
        .uik-avatar__wrapper {
          .uik-avatar__info {
            display: none;
          }
        }
        &:hover {
          .uik-avatar__avatar,
          .uik-avatar__avatarPlaceholder {
            opacity: 0.7;
          }
          .uik-avatar__wrapper {
            .uik-avatar__avatarWrapper {
              &:hover {
                .uik-avatar__avatar,
                .uik-avatar__avatarPlaceholder {
                  opacity: 1;
                }
              }
            }
          }
        }
        .uik-avatar__wrapper {
          transition: ease-out 120ms;
          .uik-avatar__avatarWrapper {
            border: solid 2px white;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: white;
            .uik-avatar__avatar,
            .uik-avatar__avatarPlaceholder {
              transition: all 0.2s;
            }
          }
          & + .uik-avatar__wrapper {
            margin: 0px 0 0 -15px;
          }
          &:hover {
            z-index: 1;
            & + .uik-avatar__wrapper {
              margin: 0px 5px 0 -10px;
            }
            .uik-avatar__avatar,
            .uik-avatar__avatarPlaceholder {
              transition: all 0.2s;
              box-shadow: 0 0 8px 4px rgba(0, 0, 0, 0.1);
            }
          }
        }
      }
      .uik-tag__wrapper {
        .uik-tag__content {
          white-space: nowrap;
        }
      }
      .uik-progress-bar__wrapper {
          width: 100px;
          margin: 0 auto;
          border-radius: 4px;
          height: 8px;
          background: rgba(0,0,0,.075);
          .uik-progress-bar__progressLine {
              height: 8px;
              border-radius: inherit;
              background: $primary;
          }
      }
      .uik-checkbox__wrapper {
        &.uik-checkbox__toggle {
          .uik-checkbox__label {
            display: flex;
            &::before {
              margin-right: 0;
            }
          }
          .uik-checkbox__description {
            margin-top: 0;
            margin-left: 0;
          }
        }
        &:not(.uik-checkbox__toggle):not(.uik-checkbox__radio) {
          .uik-checkbox__label {
            display: flex;
            &::before {
              margin-right: 0;
              transform: none;
            }
          }
          .uik-checkbox__checkbox {
            &:checked + .uik-checkbox__label {
              .uik-checkbox__checkboxIcon {
                top: 5px;
              }
            }
          }
        }
      }
      & > .uik-btn__base,
      & > .uik-tooltip,
      & > .uik-table-actions {
        & + .uik-btn__base,
        & + .uik-tooltip,
        & + .uik-table-actions {
          margin-left: 8px;
        }
      }
      & > .uik-btn__base,
      & > * > .uik-btn__base {
        padding: 0 10px;
        &.uik-btn__iconOnly {
          flex-basis: 30px;
          width: 30px;
          padding: 0;
        }
      }
      .uik-input__wrapper {
        .uik-input__input {
          margin-bottom: 0;
        }
      }
      .uik-input__wrapper,
      .uik-select__wrapper {
        .uik-input__label-container {
          margin-top: 0;
        }
        & + .uik-input__wrapper,
        & + .uik-select__wrapper {
          margin-left: 15px;
        }
      }
    }
  }
  &:first-child {
    .uik-table-cell__content {
      padding-left: 20px;
    }
  }
  &:last-child {
    .uik-table-cell__content {
      padding-right: 20px;
    }
  }
  &__sticky {
    position: sticky;
    left: 0;
    z-index: 1;
    background: inherit;
  }
  &__seamless {
    .uik-table-cell__container {
      padding: 0;
      flex-flow: column nowrap;
      justify-content: center;
      align-items: center;
      .uik-input__wrapper {
        height: 100%;
        width: 100%;
        flex-grow: 1;
        display: flex;
        flex-flow: column nowrap;
        justify-content: center;
        align-items: center;
        .uik-input__inputWrapper {
          height: 100%;
          flex-grow: 1;
          width: 100%;
          display: flex;
          flex-flow: column nowrap;
          justify-content: center;
          align-items: center;
          .uik-input__input {
            height: 100%;
            width: 100%;
            flex-grow: 1;
            border: solid 1px transparent;
            background: transparent;
            border-radius: 5px;
            &:hover {
              background: white;
              border-color: $borderColor;
            }
            &:focus {
              background: lighten($primary, 50%);
              border-color: rgba($primary, 0.5);
              border-style: dashed;
            }
          }
        }
        .uik-input__label-container {
          display: none;
        }
      }
      .uik-select__wrapper {
        height: 100%;
        width: 100%;
        flex-grow: 1;
        display: flex;
        flex-flow: column nowrap;
        justify-content: center;
        align-items: center;
        .uik-select__valueRendered {
          box-shadow: none;
          height: 100%;
          width: 100%;
          flex-grow: 1;
          border: solid 1px transparent;
          background: transparent;
          border-radius: 5px;
          &:hover {
            background: white;
            border-color: $borderColor;
          }
          &:focus {
            background: lighten($primary, 50%);
            border-color: rgba($primary, 0.5);
            border-style: dashed;
          }
        }
        .uik-input__label-container {
          display: none;
        }
      }
    }
  }
  @media only #{$mediaDesktopSmall} {
    &__hide--desktop-small {
      display: none;
    }
  }
  @media only #{$mediaTablet} {
    &__hide--tablet {
      display: none;
    }
  }
  @media only #{$mediaMobile} {
    &__hide--mobile {
      display: none;
    }
  }
  &--loading {
    .uik-table-cell__container {
      .uik-table-cell__content {
        transition: all 0.2s;
        opacity: 0;
        transform: scale(0.5);
      }
      .uik-table-cell__loader {
        position: absolute;
        animation: table-cell-loader 0.2s;
        @keyframes table-cell-loader {
          from {
            opacity: 0;
            transform: scale(0);
          }
           to {
             opacity: 1;
             transform: scale(0.45);
           }
        }
      }
    }
  }
}
</style>
